import MuiAsyncAutocomplete from "@/components/MuiAsyncAutocomplete"
import { useUrlParams } from "@/hooks/useUrlParams"
import { addMonths, addWeeks, format, parseISO, subMonths, subWeeks } from "date-fns"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { DEFAULTS, VIEW_MODES } from "../constants"
import {
  fetchInitialValues as fetchInitialValuesUtil,
  getAutoCompleteEndpoint,
  getDefaultValues,
  getInitialFilters,
  updateFiltersAndURL,
} from "../utils/filterUtils"

/**
 * Custom hook for managing calendar filters and navigation.
 * (Excludes "view" from URL params and uses window.location for route changes.)
 */
export const useCalendarFilters = () => {
  const { searchParams, clearUrlParams } = useUrlParams()

  // "view" is no longer retrieved from searchParams.
  const { tutor_id, school_id, group_id, type, date } = useMemo(() => getInitialFilters(searchParams), [searchParams])

  const defaultValues = useMemo(
    () => getDefaultValues({ tutor_id, school_id, group_id, type, date }),
    [tutor_id, school_id, group_id, type, date]
  )

  const [selectedValues, setSelectedValues] = useState(defaultValues)
  const [validationError, setValidationError] = useState(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const initDate = useMemo(() => {
    try {
      return date ? format(parseISO(date), DEFAULTS.DATE_FORMAT) : format(new Date(), DEFAULTS.DATE_FORMAT)
    } catch (error) {
      return format(new Date(), DEFAULTS.DATE_FORMAT)
    }
  }, [date])

  const [currentDate, setCurrentDate] = useState(initDate)

  // Determine view mode based on the current window location pathname.
  const viewMode = useMemo(() => {
    return window.location.pathname.includes("monthly") ? VIEW_MODES.MONTH : VIEW_MODES.WEEK
  }, [])

  // Helper function: Build query string filtering out keys with null, "null", or "undefined".
  const buildQueryString = paramsObj => {
    const search = new URLSearchParams()
    Object.keys(paramsObj).forEach(key => {
      const value = paramsObj[key]
      if (value !== null && value !== "null" && value !== "undefined") {
        search.set(key, value)
      }
    })
    return search.toString()
  }

  // Load initial filter values asynchronously if IDs exist.
  useEffect(() => {
    if (!isInitialLoad) return
    let isMounted = true
    const loadInitialValues = async () => {
      const updates = {}
      try {
        if (tutor_id) {
          const tutorResult = await fetchInitialValuesUtil("tutors", "tutor_id", tutor_id)
          if (tutorResult?.value) updates.tutor = tutorResult.value
        }
        if (school_id) {
          const schoolResult = await fetchInitialValuesUtil("schools", "school_id", school_id)
          if (schoolResult?.value) updates.school = schoolResult.value
        }
        if (group_id && school_id) {
          const groupResult = await fetchInitialValuesUtil("groups", "group_id", group_id, { school_id })
          if (groupResult?.value) updates.group = groupResult.value
        }
        if (Object.keys(updates).length || type) {
          if (isMounted) {
            setSelectedValues(prev => ({ ...prev, ...updates, ...(type ? { type } : {}) }))
          }
        }
      } catch (error) {
        setValidationError("Failed to load filter values")
      } finally {
        if (isMounted) setIsInitialLoad(false)
      }
    }
    loadInitialValues()
    return () => {
      isMounted = false
    }
  }, [tutor_id, school_id, group_id, type, isInitialLoad])

  // Update the date in state and sync it with URL query parameters.
  const updateDateAndUrl = useCallback(newDate => {
    setCurrentDate(newDate)
    const currentParams = Object.fromEntries(new URLSearchParams(window.location.search))
    const updatedParams = { ...currentParams, date: newDate }
    const queryString = buildQueryString(updatedParams)
    const newUrl = window.location.pathname + (queryString ? "?" + queryString : "")
    window.history.replaceState(null, "", newUrl)
  }, [])

  const handleNavigate = useCallback(
    direction => {
      const baseDate = parseISO(currentDate)
      const newDate =
        viewMode === VIEW_MODES.WEEK
          ? format(direction === "next" ? addWeeks(baseDate, 1) : subWeeks(baseDate, 1), DEFAULTS.DATE_FORMAT)
          : format(direction === "next" ? addMonths(baseDate, 1) : subMonths(baseDate, 1), DEFAULTS.DATE_FORMAT)
      updateDateAndUrl(newDate)
    },
    [currentDate, viewMode, updateDateAndUrl]
  )

  const handleNavigateToday = useCallback(() => {
    const today = format(new Date(), DEFAULTS.DATE_FORMAT)
    updateDateAndUrl(today)
  }, [updateDateAndUrl])

  const handleNavigatePrev = useCallback(() => handleNavigate("prev"), [handleNavigate])
  const handleNavigateNext = useCallback(() => handleNavigate("next"), [handleNavigate])

  // Change view by redirecting to a different route, preserving filtered query params.
  const handleViewChange = useCallback(
    newView => {
      if (newView === viewMode) return
      const currentParams = Object.fromEntries(new URLSearchParams(window.location.search))
      const queryString = buildQueryString(currentParams)
      if (newView === VIEW_MODES.WEEK) {
        window.location.href = "/admin/calendar/weekly" + (queryString ? "?" + queryString : "")
      } else if (newView === VIEW_MODES.MONTH) {
        window.location.href = "/admin/calendar/monthly" + (queryString ? "?" + queryString : "")
      }
    },
    [viewMode]
  )

  // Update filters (excluding "view") and merge changes into the URL.
  const updateFilter = useCallback(
    newValues => {
      const updatedValues = { ...selectedValues, ...newValues }
      const isDifferent = Object.keys(updatedValues).some(key => updatedValues[key] !== selectedValues[key])
      if (isDifferent) {
        setSelectedValues(updatedValues)
        updateFiltersAndURL(updatedValues, setValidationError, params => {
          const queryString = buildQueryString(params)
          const newUrl = window.location.pathname + (queryString ? "?" + queryString : "")
          window.history.replaceState(null, "", newUrl)
        })
      }
    },
    [selectedValues]
  )

  const handleFilterChange = useCallback(newValues => updateFilter(newValues), [updateFilter])

  const handleSchoolChange = useCallback(school => updateFilter({ school, group: null }), [updateFilter])

  const [resetCounter, setResetCounter] = useState(0)

  // Reset filters by clearing URL params and rebuilding fresh defaults.
  const handleResetFilters = useCallback(() => {
    clearUrlParams()
    const resetValues = getDefaultValues({})
    resetValues.date = currentDate
    setSelectedValues(resetValues)
    setResetCounter(prev => prev + 1)
  }, [clearUrlParams, currentDate])

  const handleTypeChange = useCallback(event => updateFilter({ type: event.target.value }), [updateFilter])

  const renderAutocomplete = useCallback(
    (label, field) => {
      const searchField = field.replace("_id", "")
      const selectedValue = selectedValues[searchField]
      const isGroupField = field === "group_id"
      const isDisabled = isGroupField && !selectedValues.school

      return (
        <MuiAsyncAutocomplete
          key={`${field}-${resetCounter}-${selectedValues.school?.value ?? "none"}`}
          source={getAutoCompleteEndpoint(`${searchField}s`)}
          placeholder={isDisabled ? "Select a school first" : `${label}: Select`}
          fieldName={field}
          selected={selectedValue}
          disabled={isDisabled}
          onSelect={newValue =>
            field === "school_id" ? handleSchoolChange(newValue) : updateFilter({ [searchField]: newValue })
          }
          externalParams={isGroupField ? { school_id: selectedValues.school?.value } : undefined}
          defaultOptions={isGroupField && !isDisabled}
          omitEmptyQuery={!(isGroupField && !isDisabled)}
        />
      )
    },
    [selectedValues, resetCounter, handleSchoolChange, updateFilter]
  )

  const activeFilters = useMemo(() => Object.values(selectedValues).filter(Boolean).length > 1, [selectedValues])
  const needsFilterWarning = useMemo(
    () => viewMode === VIEW_MODES.MONTH && !selectedValues.school && !selectedValues.tutor,
    [viewMode, selectedValues.school, selectedValues.tutor]
  )

  return {
    selectedValues,
    validationError,
    handleFilterChange,
    handleSchoolChange,
    handleResetFilters,
    handleTypeChange,
    renderAutocomplete,
    activeFilters,
    needsFilterWarning,
    isLoading: isInitialLoad,
    currentDate,
    viewMode,
    handleNavigateToday,
    handleNavigatePrev,
    handleNavigateNext,
    handleViewChange,
  }
}
